/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useQuery } from '@apollo/client';

import Imgix from 'js/components/Imgix';
import { SHORT_MONTH_DAY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils/DateTimeUtils';

import { Typography2, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { CourseAutoenrollmentModalQuery } from 'bundles/enterprise-legacy-learner-home/constants/ProgramCommonGraphqlQueries';
import type {
  CourseAutoenrollmentModalQuery as CourseAutoenrollmentModalQueryData,
  CourseAutoenrollmentModalQueryVariables,
  CourseAutoenrollmentModalQuery_ProgramProductMetadataV1Resource_multiGet_elements_metadataType_ProgramProductMetadataV1_courseMember as CourseMemberMetadata,
} from 'bundles/enterprise-legacy-learner-home/constants/__generated__/CourseAutoenrollmentModalQuery';
import type { ProductCardCourseFragmentFragment as Course } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ProductCoursesQuery';
import { CourseAutoenrollmentSessionDatesQuery } from 'bundles/enterprise-legacy-learner-home/utils/ProgramHomeGraphqlQueries';
import type {
  CourseAutoenrollmentSessionDatesQuery as CourseAutoenrollmentSessionDatesQueryData,
  CourseAutoenrollmentSessionDatesQueryVariables,
  CourseAutoenrollmentSessionDatesQuery_EnterpriseProgramSessionAssociationsV1Resource_byProgramAndCourses_elements_session as Session,
} from 'bundles/enterprise-legacy-learner-home/utils/__generated__/CourseAutoenrollmentSessionDatesQuery';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import { CourseBadge, CreditBadge } from 'bundles/enterprise-ui/components/Badges';
import { useCampusWordingForLearner } from 'bundles/unified-common/hooks/useCampusWordingForLearner';
import Skeleton from 'bundles/unified-home-common/components/shared/Skeleton';

const IMAGE_DIMENSION = 80;

type SelectedForCreditMap = {
  [courseId: string]: boolean;
};

type PropsFromCaller = {
  autoEnrolledCoursesWithData: Course[];
  programId: string;
  userId: number;
};

type PropsFromMetadataGraphql = {
  coursesSelectedForCreditMap?: SelectedForCreditMap;
};

type PropsFromSessionDatesGraphql = {
  sessionDates?: Array<Session>;
};

type Props = Pick<PropsFromCaller, 'autoEnrolledCoursesWithData'> &
  PropsFromMetadataGraphql &
  PropsFromSessionDatesGraphql;

const styles = {
  skeletonImage: (theme: Theme) => ({
    backgroundColor: 'var(--cds-color-grey-25)',
    borderRadius: 3,
    height: IMAGE_DIMENSION,
    marginRight: 'var(--cds-spacing-200)',
  }),
  productDescription: (theme: Theme) => ({
    display: 'flex',
    flexFlow: 'row nowrap',
    borderBottom: `1px solid var(--cds-color-grey-50)`,
    alignItems: 'center',
    marginTop: 'var(--cds-spacing-150)',
    paddingBottom: 'var(--cds-spacing-150)',
  }),
  pillSection: (theme: Theme) => ({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginTop: 'var(--cds-spacing-50)',
  }),
  courseImage: (theme: Theme) => ({
    marginRight: 'var(--cds-spacing-200)',
    borderRadius: 3,
  }),
  sessionDates: (theme: Theme) => ({
    marginLeft: 'var(--cds-spacing-200)',
  }),
  courseList: {
    paddingLeft: 0,
  },
};

type EnrolledCourseDescriptionProps = {
  course: Course;
  isSelectedForCredit?: boolean;
  session?: Session;
  isCampusWording?: boolean;
};

export const EnrolledCourseDescription = ({
  course,
  isSelectedForCredit,
  session,
  isCampusWording,
}: EnrolledCourseDescriptionProps) => {
  const theme = useTheme();
  const partnerName = course.partners?.[0]?.name;

  return (
    <li css={styles.productDescription(theme)} data-e2e={`${course.id}-course-description`}>
      {course.promoPhoto ? (
        <Imgix
          src={course.promoPhoto}
          alt={course.name}
          maxWidth={IMAGE_DIMENSION}
          maxHeight={IMAGE_DIMENSION}
          css={styles.courseImage(theme)}
        />
      ) : (
        <Skeleton width={IMAGE_DIMENSION} bonusCSS={styles.skeletonImage(theme)} />
      )}
      <div>
        <Typography2 component="span" variant="subtitleMedium">
          {course.name}
        </Typography2>
        {partnerName ? (
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {partnerName}
          </Typography2>
        ) : (
          <Skeleton width="100px" />
        )}

        <div css={styles.pillSection(theme)}>
          <CourseBadge />
          {isSelectedForCredit && isCampusWording && <CreditBadge className="m-l-1s" />}
          {session && (
            <Typography2
              component="p"
              variant="subtitleMedium"
              css={styles.sessionDates(theme)}
              data-test="course-session-dates"
            >
              {session?.startsAt ? formatDateTimeDisplay(session.startsAt, SHORT_MONTH_DAY_DISPLAY) : ''}-
              {session?.endsAt ? formatDateTimeDisplay(session.endsAt, SHORT_MONTH_DAY_DISPLAY) : ''}
            </Typography2>
          )}
        </div>
      </div>
    </li>
  );
};

export const AutoenrolledCourseList = ({
  autoEnrolledCoursesWithData,
  coursesSelectedForCreditMap,
  sessionDates,
}: Props) => {
  const isCampusWording = useCampusWordingForLearner();
  return (
    <ol css={styles.courseList}>
      {autoEnrolledCoursesWithData.map((course) => (
        <EnrolledCourseDescription
          key={course.id}
          course={course}
          isSelectedForCredit={coursesSelectedForCreditMap?.[course.id]}
          session={sessionDates?.find((session) => session.courseId === course.id)}
          isCampusWording={isCampusWording}
        />
      ))}
    </ol>
  );
};

const AutoenrolledCourseListContainer = ({ autoEnrolledCoursesWithData, programId }: PropsFromCaller) => {
  const { data: courseAutoenrollmentModalQueryData } = useQuery<
    CourseAutoenrollmentModalQueryData,
    CourseAutoenrollmentModalQueryVariables
  >(CourseAutoenrollmentModalQuery, {
    variables: {
      metadataIds: autoEnrolledCoursesWithData.map((course) => `${programId}~VerifiedCertificate~${course.id}`),
    },
  });

  const programProductMetadata = filterExistsOrDefault(
    courseAutoenrollmentModalQueryData?.ProgramProductMetadataV1Resource?.multiGet?.elements
  );

  const coursesSelectedForCreditMap = programProductMetadata.reduce((mappedValues: SelectedForCreditMap, metadata) => {
    const courseMetadata = (metadata.metadataType as CourseMemberMetadata).course;
    // eslint-disable-next-line no-param-reassign
    mappedValues[courseMetadata.courseId] = courseMetadata.isSelectedForCredit;
    return mappedValues;
  }, {});

  const { data: courseAutoenrollmentSessionDatesQueryData } = useQuery<
    CourseAutoenrollmentSessionDatesQueryData,
    CourseAutoenrollmentSessionDatesQueryVariables
  >(CourseAutoenrollmentSessionDatesQuery, {
    variables: {
      courseIds: autoEnrolledCoursesWithData.map((course) => course.id),
      programId,
    },
  });

  const sessions = filterExistsOrDefault(
    courseAutoenrollmentSessionDatesQueryData?.EnterpriseProgramSessionAssociationsV1Resource?.byProgramAndCourses
      ?.elements
  );

  const sessionDates = filterExistsOrDefault(sessions.map((session) => session.session));

  return (
    <AutoenrolledCourseList
      coursesSelectedForCreditMap={coursesSelectedForCreditMap}
      autoEnrolledCoursesWithData={autoEnrolledCoursesWithData}
      sessionDates={sessionDates}
    />
  );
};

export default AutoenrolledCourseListContainer;
